if (!customElements.get("gift-tiers")) {
  const STORAGE_KEYS = {
    unlocked: "giftTiers:unlocked",
    broken: "giftTiers:broken"
  };

  const TIMELINE_FILL_SELECTOR = "[data-gift-fill]";
  const TIMELINE_MAX_GAP = 18;
  const TIMELINE_COMPACT_GAP = 8;
  const TIMELINE_SCALE_END = 1.06;

  // sessionStorage throws in some private modes, and none of this state is critical.
  const storage = {
    get(key) {
      try {
        return sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        sessionStorage.setItem(key, value);
      } catch (error) {}
    }
  };

  let isRequestPending = false;
  let isResyncNeeded = false;
  let lastSyncSignature = null;

  const toggleCartItemsLoading = (isLoading) => {
    document.querySelectorAll("cart-items, cart-drawer-items").forEach((element) => {
      element.classList.toggle("is-loading", isLoading);
    });
  };

  // cart.js re-renders the drawer on its own, so a sync skipped while a request was in flight runs on the fresh element.
  const finishRequest = () => {
    isRequestPending = false;
    if (!isResyncNeeded) return;

    isResyncNeeded = false;
    document.querySelector("gift-tiers")?.sync();
  };

  class GiftTiers extends HTMLElement {
    connectedCallback() {
      this.cartDrawer = document.querySelector("cart-drawer");
      this.notice = this.querySelector("[data-gift-notice]");

      this.addEventListener("click", this.onClick);
      this.addEventListener("change", this.onVariantChange);
      this.addEventListener("focusin", this.onTipFocus);
      this.track = this.querySelector("[data-gift-track]");
      this.track?.addEventListener("pointerover", this.onTipHover);
      this.track?.addEventListener("pointerleave", this.onTipLeave);
      this.track?.addEventListener("focusout", this.onTipBlur);

      if (this.track) {
        this.layoutTimeline();
        this.timelineResizeObserver = new ResizeObserver(() => this.layoutTimeline());
        this.timelineResizeObserver.observe(this.track);

        document.fonts.ready.then(() => {
          if (this.isConnected) this.layoutTimeline();
        });
      }

      this.hideBrokenTiers();
      this.celebrateNewUnlocks();
      customElements.whenDefined("cart-drawer").then(() => this.sync());
    }

    disconnectedCallback() {
      clearTimeout(this.noticeTimer);
      this.timelineResizeObserver?.disconnect();
    }

    layoutTimeline() {
      const width = this.track.getBoundingClientRect().width;
      if (!width) return;

      const milestones = [...this.track.querySelectorAll("[data-threshold]")].sort(
        (first, second) => Number(first.dataset.threshold) - Number(second.dataset.threshold)
      );

      if (!milestones.length) return;

      const thresholds = milestones.map((milestone) => Number(milestone.dataset.threshold));
      const fill = this.track.querySelector(TIMELINE_FILL_SELECTOR);
      const railWidth = fill.offsetWidth;
      const inset = -fill.offsetLeft;
      const sizes = this.measureMilestones(milestones, width, railWidth, inset);
      const scaleMax = thresholds[thresholds.length - 1] * TIMELINE_SCALE_END;
      const positions = thresholds.map((threshold) => (threshold / scaleMax) * width);
      const firstPosition = Math.max(0, sizes[0] / 2 - inset);
      const lastIndex = positions.length - 1;
      const lastPosition = Math.min(positions[lastIndex], width - Math.max(0, sizes[lastIndex] / 2 - inset));
      const gaps = sizes.slice(1).map((size, index) => (size + sizes[index]) / 2);

      const spacing = Math.min(
        TIMELINE_MAX_GAP,
        Math.max(0, (lastPosition - firstPosition - gaps.reduce((sum, gap) => sum + gap, 0)) / Math.max(1, gaps.length))
      );

      positions[0] = Math.max(positions[0], firstPosition);

      for (let index = 1; index < positions.length; index++) {
        positions[index] = Math.max(positions[index], positions[index - 1] + gaps[index - 1] + spacing);
      }

      positions[lastIndex] = lastPosition;

      for (let index = lastIndex - 1; index >= 0; index--) {
        positions[index] = Math.min(positions[index], positions[index + 1] - gaps[index] - spacing);
      }

      milestones.forEach((milestone, index) => this.positionMilestone(milestone, positions[index], width));

      const amount = Number(this.dataset.eligibleTotal);
      let previousThreshold = 0;
      let previousPosition = 0;
      let fillPosition = railWidth;

      for (let index = 0; index <= thresholds.length; index++) {
        const threshold = thresholds[index] ?? scaleMax;
        const position = index < positions.length ? inset + positions[index] : railWidth;

        if (amount <= threshold) {
          const positionDelta = position - previousPosition;
          const thresholdProgress = (amount - previousThreshold) / (threshold - previousThreshold);
          fillPosition = previousPosition + positionDelta * thresholdProgress;
          break;
        }

        previousThreshold = threshold;
        previousPosition = position;
      }

      fill.style.transform = `scaleX(${Math.max(0, Math.min(1, fillPosition / railWidth))})`;
    }

    onClick = (event) => {
      const milestone = event.target.closest("[data-tip]");
      if (milestone) {
        this.showTip(milestone.dataset.tip);
        return;
      }

      const button = event.target.closest("[data-gift-add]");
      if (!button) return;

      const variantSelect = button.closest(".gift-tiers__option").querySelector("[data-gift-variant]");
      this.addGift(variantSelect?.value ?? button.dataset.variantId, button.dataset.tierId);
    };

    onVariantChange = (event) => {
      const select = event.target.closest("[data-gift-variant]");
      if (!select) return;

      const price = select.closest(".gift-tiers__option").querySelector("[data-gift-price]");
      price.textContent = select.selectedOptions[0].dataset.price;
    };

    // Touch pointers also fire pointerleave on release, which would close the tip right after a tap.
    onTipHover = (event) => {
      const milestone = event.target.closest("[data-tip]");
      if (milestone && event.pointerType === "mouse") this.showTip(milestone.dataset.tip);
    };

    onTipLeave = (event) => {
      if (event.pointerType === "mouse") this.showTip(this.dataset.defaultTip);
    };

    onTipFocus = (event) => {
      const milestone = event.target.closest("[data-tip]");
      if (milestone?.matches(":focus-visible")) this.showTip(milestone.dataset.tip);
    };

    onTipBlur = (event) => {
      if (!this.track.contains(event.relatedTarget)) this.showTip(this.dataset.defaultTip);
    };

    showTip(tierId) {
      this.querySelectorAll("[data-tip]").forEach((milestone) => {
        milestone.classList.toggle("is-active", milestone.dataset.tip === tierId);
      });
      this.querySelectorAll("[data-tip-panel]").forEach((panel) => {
        panel.hidden = panel.dataset.tipPanel !== tierId;
      });
    }

    getSections() {
      return this.cartDrawer.getSectionsToRender().map((section) => section.section);
    }

    addGift(variantId, tierId) {
      if (isRequestPending) return;
      isRequestPending = true;
      this.classList.add("is-loading");
      const isInDrawer = Boolean(this.closest("cart-drawer"));

      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";

      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", 1);
      formData.append("properties[_gift_tier]", tierId);
      // The discount app reads a single fixed property, so it matches gifts from every tier without per-tier rules.
      formData.append("properties[_gift]", "1");
      formData.append("sections", this.getSections().join(","));
      formData.append("sections_url", window.location.pathname);

      config.body = formData;
      delete config.headers["Content-Type"];

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: "gift-tiers",
              productVariantId: variantId,
              errors: response.errors || response.description,
              message: response.message
            });

            this.showNotice(response.description || this.dataset.errorMessage);
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, { source: "gift-tiers", productVariantId: variantId, cartData: response });
          this.cartDrawer.renderContents(response, false);
          if (isInDrawer) this.cartDrawer.querySelector("[data-drawer-close]")?.focus();
        })
        .catch(() => this.showNotice(this.dataset.errorMessage))
        .finally(() => {
          this.classList.remove("is-loading");
          finishRequest();
        });
    }

    sync() {
      const { updates, broken, discounted, soldOut } = JSON.parse(this.querySelector("[data-gift-sync]").textContent);

      if (broken.length) {
        const brokenTiers = this.getBrokenTiers();
        broken.forEach((tierId) => (brokenTiers[tierId] = this.dataset.eligibleTotal));
        storage.set(STORAGE_KEYS.broken, JSON.stringify(brokenTiers));
        console.warn("[gift-tiers] A gift was not free, check the matching discount for tiers:", broken);
      }

      if (!Object.keys(updates).length) {
        lastSyncSignature = null;
        return;
      }

      if (isRequestPending) {
        isResyncNeeded = true;
        return;
      }

      // Stops a retry loop when Shopify rejects the update and the same invalid lines come back.
      const signature = JSON.stringify(updates);
      if (signature === lastSyncSignature) return;
      lastSyncSignature = signature;
      isRequestPending = true;
      // cart.js changes quantities by line index, which shifts once the sync removes a gift line.
      toggleCartItemsLoading(true);

      fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          updates,
          sections: this.getSections(),
          sections_url: window.location.pathname
        })
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) return;

          const notice = broken.length
            ? this.dataset.conflictMessage
            : (discounted && this.dataset.discountedMessage) || (soldOut && this.dataset.soldOutMessage);
          publish(PUB_SUB_EVENTS.cartUpdate, { source: "gift-tiers", cartData: response });
          this.cartDrawer.renderContents(response, false);
          if (notice) document.querySelectorAll("gift-tiers").forEach((element) => element.showNotice(notice));
        })
        .catch(() => {
          lastSyncSignature = null;
        })
        .finally(() => {
          toggleCartItemsLoading(false);
          finishRequest();
        });
    }

    measureMilestones(milestones, width, railWidth, inset) {
      this.track.classList.remove("is-compact");
      this.track.style.removeProperty("min-height");
      this.track.style.removeProperty("--label-width");

      const sizes = milestones.map((milestone) => milestone.getBoundingClientRect().width);
      const lastIndex = sizes.length - 1;
      const firstPosition = Math.max(0, sizes[0] / 2 - inset);
      const lastPosition = Math.min(width / TIMELINE_SCALE_END, width - Math.max(0, sizes[lastIndex] / 2 - inset));
      const requiredSpace = sizes.reduce((sum, size) => sum + size, 0) - (sizes[0] + sizes[lastIndex]) / 2;
      if (requiredSpace <= lastPosition - firstPosition) return sizes;

      this.track.classList.add("is-compact");

      this.track.style.setProperty(
        "--label-width",
        `${Math.max(1, railWidth / sizes.length - TIMELINE_COMPACT_GAP)}px`
      );

      this.track.style.minHeight = `${Math.max(...milestones.map((milestone) => milestone.offsetHeight))}px`;
      return milestones.map((milestone) => milestone.getBoundingClientRect().width);
    }

    positionMilestone(milestone, offset, width) {
      const position = (offset / width) * 100;
      milestone.style.setProperty("--position", `${position}%`);

      this.querySelectorAll("[data-tip-panel]").forEach((panel) => {
        if (panel.dataset.tipPanel === milestone.dataset.tip) panel.style.setProperty("--caret", position);
      });
    }

    getBrokenTiers() {
      try {
        const brokenTiers = JSON.parse(storage.get(STORAGE_KEYS.broken));
        return brokenTiers && !Array.isArray(brokenTiers) ? brokenTiers : {};
      } catch (error) {
        return {};
      }
    }

    hideBrokenTiers() {
      // A tier stays hidden only until the cart changes, so a fixed discount shows up again without a new session.
      const brokenTiers = this.getBrokenTiers();

      this.querySelectorAll("[data-gift-ready]").forEach((element) => {
        element.hidden = brokenTiers[element.dataset.giftReady] === this.dataset.eligibleTotal;
      });
    }

    celebrateNewUnlocks() {
      const unlockedCount = Number(this.dataset.unlockedCount);
      const previousCount = storage.get(STORAGE_KEYS.unlocked);
      storage.set(STORAGE_KEYS.unlocked, unlockedCount);

      if (previousCount === null || unlockedCount <= Number(previousCount)) return;
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

      this.classList.add("is-celebrating");
      this.appendChild(this.createConfetti());
      setTimeout(() => this.classList.remove("is-celebrating"), 1600);
    }

    createConfetti() {
      const burst = document.createElement("div");
      burst.className = "gift-tiers__confetti";
      burst.setAttribute("aria-hidden", "true");

      for (let index = 0; index < 24; index++) {
        const piece = document.createElement("i");
        piece.style.left = `${8 + Math.random() * 84}%`;
        piece.style.setProperty("--drift", `${(Math.random() - 0.5) * 120}px`);
        piece.style.setProperty("--spin", `${(Math.random() - 0.5) * 720}deg`);
        piece.style.animationDelay = `${Math.random() * 0.2}s`;
        burst.appendChild(piece);
      }

      setTimeout(() => burst.remove(), 1600);
      return burst;
    }

    showNotice(message) {
      if (!this.notice || !message) return;

      this.notice.textContent = message;
      clearTimeout(this.noticeTimer);

      this.noticeTimer = setTimeout(() => {
        this.notice.textContent = "";
      }, 6000);
    }
  }

  customElements.define("gift-tiers", GiftTiers);
}
